import { useEffect, useRef, useState, useCallback } from "react";

export interface LiveToken {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  market_cap: number;
  market_cap_rank: number;
  total_volume: number;
  circulating_supply: number | null;
  price_change_percentage_1h_in_currency?: number | null;
  price_change_percentage_24h_in_currency?: number | null;
  price_change_percentage_7d_in_currency?: number | null;
  sparkline_in_7d?: { price: number[] };
  last_updated?: string;
}

interface Options {
  perPage?: number;
  intervalMs?: number;
}

const COINGECKO = "https://api.coingecko.com/api/v3";

export function useLiveMarkets({ perPage = 100, intervalMs = 5000 }: Options = {}) {
  const [tokens, setTokens] = useState<LiveToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const prevPricesRef = useRef<Map<string, number>>(new Map());
  const [pricePulse, setPricePulse] = useState<Map<string, "up" | "down">>(new Map());

  const fetchOnce = useCallback(async (controller: AbortController) => {
    try {
      const url = `${COINGECKO}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${perPage}&page=1&sparkline=true&price_change_percentage=1h%2C24h%2C7d`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`CoinGecko ${res.status}`);
      const data = (await res.json()) as LiveToken[];

      // Compute pulse map
      const nextPulse = new Map<string, "up" | "down">();
      data.forEach(t => {
        const prev = prevPricesRef.current.get(t.id);
        if (prev !== undefined && prev !== t.current_price) {
          nextPulse.set(t.id, t.current_price > prev ? "up" : "down");
        }
        prevPricesRef.current.set(t.id, t.current_price);
      });
      setPricePulse(nextPulse);
      setTokens(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [perPage]);

  useEffect(() => {
    let controller = new AbortController();
    let timer: number | undefined;

    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      controller = new AbortController();
      await fetchOnce(controller);
    };

    tick();
    timer = window.setInterval(tick, intervalMs);

    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (timer) clearInterval(timer);
      controller.abort();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchOnce, intervalMs]);

  return { tokens, loading, error, lastUpdate, pricePulse, refresh: () => fetchOnce(new AbortController()) };
}
